import { Fragment, useState, useEffect, useRef } from "react";
import {
  Link,
  useLocation,
  useNavigate,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";

import { Menu, Transition } from "@headlessui/react";

import NewToolTip from "../common/New-ToolTip";
import ConfirmationModalNew from "../common/ConfirmationModalNew";
import { getCurrentUserDetails, logout, extractRoleNames, hasUserRole } from "../../redux/modules/user/actions";
import { UserRole } from "../../helpers/roles";
import { createTokenizedUrl, logTokenTransfer, encodeTokenForUrl, decodeTokenFromUrl } from "../../helpers/tokenSharing";
import { isRouteHidden } from "../../constants/navigation";
import {
  hasAccessToEnvAdministration,
  hasAccessToEnvClinical,
  canReelMemberAccessRxAdministration,
  isUserReelMember,
} from "../../helpers/roleBasedAccessHelper";

const activeClass = `border-b-[4px] border-teal-600 text-teal-600`;

const ENVIRONMENT_URLS = {
  clinical: {
    dev: "https://dev.healthcompassrx.com/",
    staging: "https://www.staging.healthcompassrx.com/",
    production: "https://www.healthcompassrx.com/",
  },
  administration: {
    dev: "https://dev-admin.healthcompassrx.com/administration?isRxAdministration=true",
    staging: "https://staging-admin.healthcompassrx.com/administration?isRxAdministration=true",
    production: "https://admin.healthcompassrx.com/administration?isRxAdministration=true",
  },
  reelAdministration: {
    dev: "https://dev-admin.healthcompassrx.com/pharmacies/banner?isRxAdministration=false",
    staging: "https://staging-admin.healthcompassrx.com/pharmacies/banner?isRxAdministration=false",
    production: "https://www.admin.healthcompassrx.com/pharmacies/banner?isRxAdministration=false",
  },
};

const Header = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [showUserOptions, setShowUserOptions] = useState(false);
  const [isSwitchModalOpen, setIsSwitchModalOpen] = useState(false);

  const [userName, setUserName] = useState(null);
  const [role, setRole] = useState(null);
  const [storeNumber, setStoreNumber] = useState(null);
  const [pharmacyName, setPharmacyName] = useState(null);
  const [profileAvatar, setProfileAvatar] = useState(null);
  const { currentLoggedInUserDetail } = useSelector(
    (state) => state.currentLoggedInUserDetail
  );
  // Use the new userRoles from Redux store instead of currentUserRoles
  const { userRoles, isAuthenticated, userInfo } = useSelector((state) => state.users);
  const [disableMenu, setDisableMenu] = useState(true);
  const [legalNotAccepted, setLegalNotAccepted] = useState(false);
  const [hasAccessToDevAdmin, setHasAccessToDevAdmin] = useState(false);
  const [hasAccessToStagingAdmin, setHasAccessToStagingAdmin] = useState(false);
  const [hasAccessToProdAdmin, setHasAccessToProdAdmin] = useState(false);
  const [hasAccessToDevClinical, setHasAccessToDevClinical] = useState(false);
  const [hasAccessToStagingClinical, setHasAccessToStagingClinical] = useState(false);
  const [hasAccessToProdClinical, setHasAccessToProdClinical] = useState(false);

  const [isBackNavigationPrompt, setIsBackNavigationPrompt] = useState(false);
  const [isCrossAppGuardActive, setIsCrossAppGuardActive] = useState(false);
  const [isClinicalsSubmenuOpen, setIsClinicalsSubmenuOpen] = useState(false);
  const [isAdministrationSubmenuOpen, setIsAdministrationSubmenuOpen] = useState(false);
  const [isReelAdministrationSubmenuOpen, setIsReelAdministrationSubmenuOpen] = useState(false);
  const [switchTarget, setSwitchTarget] = useState(null); // 'admin' | 'reelAdmin' | null
  const [isDev, setIsDev] = useState(false);
  const [isStaging, setIsStaging] = useState(false);
  const [isProduction, setIsProduction] = useState(false);

  const CROSS_APP_INFO_KEY = "clinicalCrossAppReturnInfo";
  const LAST_VISIBLE_ROUTE_KEY = "clinicalLastVisibleRoute";
  const CROSS_APP_ENTRY_ROUTE_KEY = "clinicalCrossAppEntryRoute";
  const ROUTE_STACK_KEY = "clinicalCrossAppRouteStack";
  const GUARD_STATE_KEY = "clinicalCrossAppGuardState";

  // Track cross-app navigation metadata and enforce boundary prompts.
  // Track cross-app navigation metadata and enforce boundary prompts.
  const crossAppInfoRef = useRef(null);
  const lastVisibleRouteRef = useRef(null);
  const crossAppEntryRouteRef = useRef(null);
  const routeStackRef = useRef([]);
  const modalOpenRef = useRef(false);
  const hasInjectedGuardState = useRef(false);
  const isHandlingPopStateRef = useRef(false);

  // Helper methods manage the guarded route stack so we only prompt
  // when back navigation would leave the Clinical application.
  const getCurrentRelativeRoute = () => {
    if (typeof window === "undefined") {
      return `${pathname}${search}${hash}`;
    }
    return `${window.location.pathname}${window.location.search}${window.location.hash}`;
  };

  const logGuard = (...args) => {
    if (process.env.NODE_ENV === "development") {
      console.log("[CrossAppGuard]", ...args);
    }
  };

  const sanitizeRoute = (route) => {
    if (!route || typeof route !== "string") {
      return null;
    }
    return isRouteHidden(route) ? null : route;
  };

  const setRouteStack = (stack) => {
    const sanitizedStack = Array.isArray(stack)
      ? stack.map(sanitizeRoute).filter(Boolean)
      : [];
    routeStackRef.current = sanitizedStack;
    if (typeof window === "undefined") {
      return;
    }
    if (!sanitizedStack || sanitizedStack.length === 0) {
      sessionStorage.removeItem(ROUTE_STACK_KEY);
      return;
    }
    sessionStorage.setItem(ROUTE_STACK_KEY, JSON.stringify(sanitizedStack));
  };

  const getRouteStack = () => {
    if (typeof window === "undefined") {
      return Array.isArray(routeStackRef.current) ? [...routeStackRef.current] : [];
    }

    const storedStack = sessionStorage.getItem(ROUTE_STACK_KEY);
    if (!storedStack) {
      routeStackRef.current = [];
      return [];
    }

    try {
      const parsedStack = JSON.parse(storedStack);
      const normalizedStack = Array.isArray(parsedStack)
        ? parsedStack.map(sanitizeRoute).filter(Boolean)
        : [];
      routeStackRef.current = normalizedStack;
      return [...normalizedStack];
    } catch (error) {
      routeStackRef.current = [];
      sessionStorage.removeItem(ROUTE_STACK_KEY);
      return [];
    }
  };

  const updateLastVisibleRoute = (route) => {
    const visibleRoute = sanitizeRoute(route);
    if (!visibleRoute) {
      return;
    }
    lastVisibleRouteRef.current = visibleRoute;
    if (typeof window !== "undefined") {
      sessionStorage.setItem(LAST_VISIBLE_ROUTE_KEY, visibleRoute);
    }
  };

  const getLastVisibleRoute = () => {
    if (lastVisibleRouteRef.current) {
      return lastVisibleRouteRef.current;
    }
    if (typeof window === "undefined") {
      return null;
    }
    const stored = sessionStorage.getItem(LAST_VISIBLE_ROUTE_KEY);
    const visibleRoute = sanitizeRoute(stored);
    lastVisibleRouteRef.current = visibleRoute;
    return visibleRoute;
  };

  const setCrossAppEntryRoute = (route) => {
    const visibleRoute = sanitizeRoute(route);
    if (!visibleRoute) {
      return;
    }
    crossAppEntryRouteRef.current = visibleRoute;
    if (typeof window !== "undefined") {
      sessionStorage.setItem(CROSS_APP_ENTRY_ROUTE_KEY, visibleRoute);
    }
  };

  const getCrossAppEntryRoute = () => {
    if (crossAppEntryRouteRef.current) {
      return crossAppEntryRouteRef.current;
    }
    if (typeof window === "undefined") {
      return null;
    }
    const stored = sessionStorage.getItem(CROSS_APP_ENTRY_ROUTE_KEY);
    const visibleRoute = sanitizeRoute(stored);
    crossAppEntryRouteRef.current = visibleRoute;
    return visibleRoute;
  };

  const clearCrossAppEntryRoute = () => {
    crossAppEntryRouteRef.current = null;
    if (typeof window !== "undefined") {
      sessionStorage.removeItem(CROSS_APP_ENTRY_ROUTE_KEY);
    }
  };

  const ensureRouteStackInitialized = (route) => {
    const stack = getRouteStack();
    if (stack.length === 0 && route) {
      const visibleRoute = sanitizeRoute(route);
      if (visibleRoute) {
        setRouteStack([visibleRoute]);
        updateLastVisibleRoute(visibleRoute);
        logGuard("initialize stack", visibleRoute);
      }
    }
  };

  const pushRouteToStack = (route) => {
    const visibleRoute = sanitizeRoute(route);
    if (!visibleRoute) {
      return;
    }
    const stack = getRouteStack();
    if (stack.length === 0 || stack[stack.length - 1] !== visibleRoute) {
      stack.push(visibleRoute);
      setRouteStack(stack);
      updateLastVisibleRoute(visibleRoute);
      logGuard("push route", visibleRoute, stack);
    }
  };

  const popRouteFromStack = () => {
    const stack = getRouteStack();
    if (stack.length > 0) {
      const popped = stack.pop();
      setRouteStack(stack);
      updateLastVisibleRoute(stack[stack.length - 1] || null);
      logGuard("pop route", popped, stack);
    }
    return stack;
  };

  const clearRouteStack = () => {
    setRouteStack([]);
    if (typeof window !== "undefined") {
      sessionStorage.removeItem(LAST_VISIBLE_ROUTE_KEY);
    }
    lastVisibleRouteRef.current = null;
    clearCrossAppEntryRoute();
    logGuard("clear route stack");
  };

  const { medicationReviews, minorAilments } = useSelector(
    (state) => state.clinicalService.clinicalServicesSmall
  );
  const location = useLocation();
  const { pathname, search, hash } = location;

  useEffect(() => {
    modalOpenRef.current = isSwitchModalOpen;
  }, [isSwitchModalOpen]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const relativeRoute = getCurrentRelativeRoute();
    updateLastVisibleRoute(relativeRoute);

    if (!isCrossAppGuardActive) {
      return;
    }

    if (isHandlingPopStateRef.current) {
      isHandlingPopStateRef.current = false;
      return;
    }

    pushRouteToStack(relativeRoute);
  }, [pathname, search, hash, isCrossAppGuardActive]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    const searchParams = new URLSearchParams(window.location.search);
    const encodedReturnUrl = searchParams.get("returnUrl");
    const encodedReturnApp = searchParams.get("returnApp");
    let guardEnabled = false;

    if (encodedReturnUrl && encodedReturnApp) {
      const decodedReturnUrl = decodeTokenFromUrl(encodedReturnUrl);
      const decodedReturnApp = decodeTokenFromUrl(encodedReturnApp);

      if (decodedReturnUrl && decodedReturnApp) {
        const crossAppInfo = {
          returnUrl: decodedReturnUrl,
          returnApp: decodedReturnApp,
          from: searchParams.get("from"),
          referrer: document.referrer || null,
          timestamp: Date.now(),
        };

        crossAppInfoRef.current = crossAppInfo;
        sessionStorage.setItem(CROSS_APP_INFO_KEY, JSON.stringify(crossAppInfo));
        guardEnabled = true;
      }
    }

    if (!guardEnabled) {
      const storedInfo = sessionStorage.getItem(CROSS_APP_INFO_KEY);
      if (storedInfo) {
        try {
          const parsedInfo = JSON.parse(storedInfo);
          crossAppInfoRef.current = parsedInfo;
          guardEnabled = true;
        } catch (error) {
          crossAppInfoRef.current = null;
          sessionStorage.removeItem(CROSS_APP_INFO_KEY);
        }
      }
    }

    const currentRoute = getCurrentRelativeRoute();

    if (guardEnabled) {
      ensureRouteStackInitialized(currentRoute);
      if (!getCrossAppEntryRoute() && currentRoute) {
        setCrossAppEntryRoute(currentRoute);
      }
    } else {
      clearRouteStack();
      clearCrossAppEntryRoute();
    }

    setIsCrossAppGuardActive(guardEnabled);

    if (guardEnabled) {
      sessionStorage.setItem(GUARD_STATE_KEY, "true");
    } else {
      sessionStorage.removeItem(GUARD_STATE_KEY);
      hasInjectedGuardState.current = false;
    }

    if (!guardEnabled || hasInjectedGuardState.current) {
      return;
    }

    window.history.pushState({ [GUARD_STATE_KEY]: true }, "", window.location.href);
    hasInjectedGuardState.current = true;
  }, [search]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return;
    }

    if (!isCrossAppGuardActive) {
      return;
    }

    const handlePopState = () => {
      if (!crossAppInfoRef.current) {
        return;
      }

      if (modalOpenRef.current) {
        window.history.pushState({ [GUARD_STATE_KEY]: true }, "", window.location.href);
        return;
      }

      const stack = getRouteStack();
      logGuard("popstate", stack);

      if (stack.length > 1) {
        popRouteFromStack();
        const updatedStack = getRouteStack();
        if (updatedStack.length <= 1) {
          setIsBackNavigationPrompt(true);
          setIsSwitchModalOpen(true);
          window.history.pushState({ [GUARD_STATE_KEY]: true }, "", window.location.href);
          return;
        }
        isHandlingPopStateRef.current = true;
        return;
      }

      if (stack.length <= 1) {
        setIsBackNavigationPrompt(true);
        setIsSwitchModalOpen(true);
        window.history.pushState({ [GUARD_STATE_KEY]: true }, "", window.location.href);
        return;
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [isCrossAppGuardActive]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      const hostname = window.location.hostname;
      if (hostname.includes('dev') || hostname === 'localhost') {
        setIsDev(true);
        setIsStaging(false);
        setIsProduction(false);
      } else if (hostname.includes('staging')) {
        setIsDev(false);
        setIsStaging(true);
        setIsProduction(false);
      } else {
        setIsDev(false);
        setIsStaging(false);
        setIsProduction(true);
      }
    }
  }, []);

// Determine REEL membership from current logged-in user detail.
const isReelMember = isUserReelMember(currentLoggedInUserDetail);
const adminPortalLabel = isReelMember ? "Rx Administration" : "Administration";

// Determine current environment label (Dev / Staging / Production)
const getCurrentEnvironment = () => {
  if (isDev) return "Dev";
  if (isStaging) return "Staging";
  if (isProduction) return "Production";
  return "Dev";
};

const getCurrentEnvironmentKey = () => {
  if (isDev) return "dev";
  if (isStaging) return "staging";
  return "production";
};

const getSingleClinicalEnvironment = () => {
  if (hasAccessToDevClinical) return "dev";
  if (hasAccessToStagingClinical) return "staging";
  if (hasAccessToProdClinical) return "production";
  return getCurrentEnvironmentKey();
};

  const currentEnvironment = getCurrentEnvironment();

  const clinicalAccessCount =
    Number(hasAccessToDevClinical) +
    Number(hasAccessToStagingClinical) +
    Number(hasAccessToProdClinical);
  const hasSingleClinicalAccess = clinicalAccessCount === 1;
  const hasMultipleClinicalAccess = clinicalAccessCount > 1;

  const adminAccessCount =
    Number(hasAccessToDevAdmin) +
    Number(hasAccessToStagingAdmin) +
    Number(hasAccessToProdAdmin);
  const hasAnyClinicalAccess = clinicalAccessCount > 0;
  const hasSingleAdminAccess = adminAccessCount === 1;
  const hasMultipleAdminAccess = adminAccessCount > 1;
  const hasCurrentEnvAdminAccess =
    (isDev && hasAccessToDevAdmin) ||
    (isStaging && hasAccessToStagingAdmin) ||
    (isProduction && hasAccessToProdAdmin);

  const canAccessRxAdministration =
    hasCurrentEnvAdminAccess &&
    (!isReelMember || canReelMemberAccessRxAdministration(currentLoggedInUserDetail?.companyRole));
  const canAccessReelAdministration = isReelMember && adminAccessCount > 0;


  // Authentication check is now handled centrally in App.jsx
  // This useEffect is kept for backward compatibility but the main logic is in App.jsx
  useEffect(() => {
    if (isAuthenticated && userInfo?.userId && !currentLoggedInUserDetail) {
      dispatch(getCurrentUserDetails(userInfo.userId));
    }
  }, [isAuthenticated, userInfo?.userId, currentLoggedInUserDetail, dispatch]);


  useEffect(() => {
    if (currentLoggedInUserDetail) {
      if (currentLoggedInUserDetail.acceptedTermOfUse && currentLoggedInUserDetail.acceptedPrivacyPolicy) {
        setDisableMenu(false);
        setLegalNotAccepted(false);
      } else {
        setDisableMenu(true);
        setLegalNotAccepted(true);
      }
    }
  }, [currentLoggedInUserDetail]);


  useEffect(() => {
    if (isAuthenticated && userInfo) {
      // Set user name from userInfo or currentLoggedInUserDetail
      const name = currentLoggedInUserDetail?.firstName || userInfo?.firstName || "No name available";
      setUserName(name);

      // Set role from userRoles (now structured objects)
      const roleNames = extractRoleNames(userRoles);
      const firstRole = roleNames[0];
      const formattedRole = firstRole?.replace(/([a-z])([A-Z])/g, '$1 $2');
      setRole(formattedRole);

      // Check access to Staging environment (Admin OR Clinical)
      // Note: Property name is camelCase (companyRole), not PascalCase (CompanyRole)
      const companyRole = currentLoggedInUserDetail?.companyRole;
      const [hasDevAdmin, hasStagingAdmin, hasProdAdmin] =
        hasAccessToEnvAdministration(companyRole);
      const [hasDevClinical, hasStagingClinical, hasProdClinical] =
        hasAccessToEnvClinical(companyRole);

      // Check access flags (all in camelCase)
      setHasAccessToDevAdmin(hasDevAdmin);
      setHasAccessToStagingAdmin(hasStagingAdmin);
      setHasAccessToProdAdmin(hasProdAdmin);
      setHasAccessToDevClinical(hasDevClinical);
      setHasAccessToStagingClinical(hasStagingClinical);
      setHasAccessToProdClinical(hasProdClinical);

      // Set store and pharmacy info from currentLoggedInUserDetail
      setStoreNumber(currentLoggedInUserDetail?.storeNumber || "");
      setPharmacyName(currentLoggedInUserDetail?.pharmacyName || "");

      // Generate profile avatar
      const firstName = currentLoggedInUserDetail?.firstName || userInfo?.firstName || "";
      const lastName = currentLoggedInUserDetail?.lastName || userInfo?.lastName || "";
      setProfileAvatar(userPicture(firstName, lastName));
    }
  }, [isAuthenticated, userInfo, currentLoggedInUserDetail, userRoles]);

  const userPicture = (firstName, lastName) => {
    const fInitial = firstName?.[0]?.toUpperCase() || "?";
    const isReelOutline = lastName?.toLowerCase() === "reeloutline";
    const lInitial = !isReelOutline ? lastName?.[0]?.toUpperCase() || "?" : "";

    const svg = `
        <svg xmlns="http://www.w3.org/2000/svg" width="40" height="40">
          <circle cx="20" cy="20" r="20" fill="#017E9D" />
          <text x="20" y="26" text-anchor="middle" font-size="16" fill="white" font-family="sans-serif">
          ${fInitial}${lInitial}
          </text>
        </svg>
      `;

    return `data:image/svg+xml;base64,${btoa(svg)}`;
  };


  const classNames = (...classes) => {
    return classes.filter(Boolean).join(" ");
  };

  const checkAnyRunningService = () => {
    return (
      medicationReviews?.some((value) => value.status === "inProgress") ||
      minorAilments?.some((value) => value.status === "inProgress")
    );
  };

  const getReturnMetadata = () => {
    return {
      returnUrl: window.location.href,
      returnApp: "clinical",
    };
  };

  const withReturnMetadata = (targetUrl) => {
    try {
      const metadata = getReturnMetadata();
      const urlInstance = new URL(targetUrl);
      Object.entries(metadata).forEach(([key, value]) => {
        const encodedValue = encodeTokenForUrl(value);
        if (encodedValue) {
          urlInstance.searchParams.set(key, encodedValue);
        }
      });
      return urlInstance.toString();
    } catch (error) {
      return targetUrl;
    }
  };

  const clearCrossAppInfo = () => {
    crossAppInfoRef.current = null;
    if (typeof window !== "undefined") {
      sessionStorage.removeItem(CROSS_APP_INFO_KEY);
      sessionStorage.removeItem(GUARD_STATE_KEY);
    }
    clearRouteStack();
    setIsCrossAppGuardActive(false);
    hasInjectedGuardState.current = false;
  };

  const restoreLastInternalRoute = () => {
    if (typeof window === "undefined") {
      return;
    }
    const currentRelativeRoute = getCurrentRelativeRoute();
    const stack = getRouteStack();
    const lastVisibleRoute = stack.length > 0 ? stack[stack.length - 1] : getLastVisibleRoute();

    if (lastVisibleRoute && lastVisibleRoute !== currentRelativeRoute) {
      isHandlingPopStateRef.current = true;
      navigate(lastVisibleRoute, { replace: true });
    }

    setTimeout(() => {
      if (typeof window !== "undefined") {
        window.history.pushState({ [GUARD_STATE_KEY]: true }, "", window.location.href);
      }
    }, 0);
  };

  const handleStayInClinical = () => {
    setIsSwitchModalOpen(false);
    if (isBackNavigationPrompt) {
      restoreLastInternalRoute();
      setIsBackNavigationPrompt(false);
    }
  };

  const handleModalClose = () => {
    handleStayInClinical();
  };

  const handleClinicalNavigation = (environment) => {
    const targetEnvironment = environment || getSingleClinicalEnvironment();
    const currentEnvironmentKey = getCurrentEnvironmentKey();

    if (targetEnvironment === currentEnvironmentKey) {
      setIsBackNavigationPrompt(false);
      setSwitchTarget(isReelMember ? "reelAdmin" : "admin");
      setIsSwitchModalOpen(true);
      return;
    }

    const targetUrl = ENVIRONMENT_URLS.clinical[targetEnvironment];
    if (targetUrl) {
      window.location.href = targetUrl;
    }
  };

  const handleAdministrationNavigation = (environment) => {
    const targetEnvironment = environment || getCurrentEnvironmentKey();
    const currentEnvironmentKey = getCurrentEnvironmentKey();

    if (targetEnvironment === currentEnvironmentKey) {
      handleAdminClick();
      return;
    }

    const targetUrl = ENVIRONMENT_URLS.administration[targetEnvironment];
    if (targetUrl) {
      window.location.href = targetUrl;
      return;
    }

    handleAdminClick();
  };

  const handleAdminSingleAccessClick = () => {
    if (hasAccessToDevAdmin) {
      handleAdministrationNavigation('dev');
      return;
    }
    if (hasAccessToStagingAdmin) {
      handleAdministrationNavigation('staging');
      return;
    }
    if (hasAccessToProdAdmin) {
      handleAdministrationNavigation('production');
      return;
    }

    handleAdministrationNavigation('dev');
  };

  const handleReelAdminSingleAccessClick = () => {
    if (hasAccessToDevAdmin) {
      handleReelAdministrationClick('dev');
      return;
    }
    if (hasAccessToStagingAdmin) {
      handleReelAdministrationClick('staging');
      return;
    }
    if (hasAccessToProdAdmin) {
      handleReelAdministrationClick('production');
      return;
    }

    handleReelAdministrationClick('dev');
  };

  const handleConfirmAdminSwitch = () => {
    if (isBackNavigationPrompt) {
      setIsBackNavigationPrompt(false);
    }
    clearRouteStack();
    clearCrossAppInfo();
    setIsSwitchModalOpen(false);
    setSwitchTarget(null);
    confirmSwitchToAdmin();
  };

  const handleReelAdministrationClick = (environment) => {
    setIsBackNavigationPrompt(false);
    const targetEnvironment = environment || getCurrentEnvironmentKey();
    const currentEnvironmentKey = getCurrentEnvironmentKey();

    if (targetEnvironment === currentEnvironmentKey) {
      setSwitchTarget('reelAdmin');
      setIsSwitchModalOpen(true);
      return;
    }

    const targetUrl = ENVIRONMENT_URLS.reelAdministration[targetEnvironment];
    if (targetUrl) {
      window.location.href = targetUrl;
    }
  };

  const handleConfirmReelAdminSwitch = async () => {
    if (isBackNavigationPrompt) {
      setIsBackNavigationPrompt(false);
    }
    clearRouteStack();
    clearCrossAppInfo();
    setIsSwitchModalOpen(false);
    setSwitchTarget(null);
    
    // Get the current access token and user info from sessionStorage
    const accessToken = sessionStorage.getItem('accessToken');
    const userInfo = sessionStorage.getItem('userInfo');
    const additionalParams = getReturnMetadata();
    
    // Set REEL Admin mode flag
    try {
      sessionStorage.setItem('reelAdminMode', 'true');
      sessionStorage.setItem('reelAdminRedirectTarget', '/pharmacies/banner?isRxAdministration=false');
    } catch (e) {
      // no-op
    }

    const lastLoginForAdmin = sessionStorage.getItem('lastLoginForAdmin');
    const now = Date.now();
    const isLoginRequired = lastLoginForAdmin ? now - parseInt(lastLoginForAdmin) >= 15 * 60 * 1000 : true;
    
    if (isLoginRequired) {
      const timestamp = Date.now().toString();
      const urlInstance = new URL(import.meta.env.VITE_ADMIN_APP_DOMAIN);
      urlInstance.searchParams.set("loginRequired", "true");
      urlInstance.searchParams.set("moveDirect", "true");
      urlInstance.searchParams.set("ts", timestamp);
      urlInstance.searchParams.set("returnTo", encodeURIComponent('/pharmacies/banner?isRxAdministration=false'));
      Object.entries(additionalParams).forEach(([key, value]) => {
        const encodedValue = encodeTokenForUrl(value);
        if (encodedValue) {
          urlInstance.searchParams.set(key, encodedValue);
        }
      });
      window.location.href = urlInstance.toString();
      return;
    }

    if (accessToken) {
      // Create tokenized URL for seamless switching to REEL Admin
      const adminUrl = createTokenizedUrl(
        `${import.meta.env.VITE_ADMIN_APP_DOMAIN}/pharmacies/banner?isRxAdministration=false`,
        accessToken,
        'clinical',
        userInfo,
        { ...additionalParams, returnTo: '/pharmacies/banner?isRxAdministration=false' }
      );

      // Log the token transfer for debugging
      logTokenTransfer('Header - Switch to REEL Admin', 'clinical', true);

      // Redirect to admin with token
      window.location.href = adminUrl;
    } else {
      // Fallback: redirect without token (user will need to login)
      console.warn('No access token found, redirecting to admin login');
      const fallbackUrl = withReturnMetadata(import.meta.env.VITE_ADMIN_APP_DOMAIN + "/pharmacies/banner?isUserLoggedInToClinical=true");
      window.location.href = fallbackUrl;
    }
  };

  const handleAdminClick = () => {
    setIsBackNavigationPrompt(false);
    setSwitchTarget('admin');
    // Check if user is already logged in to admin
    const isUserLoggedInToAdmin = sessionStorage.getItem("isUserLoggedInToAdmin");

    if (isUserLoggedInToAdmin) {
      // User is already logged in to admin. Use tokenized redirect when possible so
      // the admin app refreshes cross-app session timestamps for return to Clinical.
      const accessToken = sessionStorage.getItem('accessToken');
      const userInfo = sessionStorage.getItem('userInfo');
      const additionalParams = getReturnMetadata();

      if (accessToken) {
        const adminUrl = createTokenizedUrl(
          `${import.meta.env.VITE_ADMIN_APP_DOMAIN}/administration/pharmacy-stores?isRxAdministration=true`,
          accessToken,
          'clinical',
          userInfo,
          additionalParams
        );
        window.location.href = adminUrl;
        return;
      }

      const destination = withReturnMetadata(import.meta.env.VITE_ADMIN_APP_DOMAIN + "/administration/pharmacy-stores?isRxAdministration=true");
      window.location.href = destination;
    } else {
      // Show modal for everyone; content differs by membership
      setIsSwitchModalOpen(true);
    }
  };

  const confirmSwitchToAdmin = () => {
    // Get the current access token and user info from sessionStorage
    const accessToken = sessionStorage.getItem('accessToken');
    const userInfo = sessionStorage.getItem('userInfo');

    const lastLoginForAdmin = sessionStorage.getItem('lastLoginForAdmin');
    const now = Date.now();
    const isLoginRequired = lastLoginForAdmin ? now - parseInt(lastLoginForAdmin) >= 15 * 60 * 1000 : true;
    const additionalParams = getReturnMetadata();
    const targetRxAdminRoute = '/administration/pharmacy-stores?isRxAdministration=true';
    
    if (isLoginRequired) {
      const timestamp = Date.now().toString();
      const urlInstance = new URL(import.meta.env.VITE_ADMIN_APP_DOMAIN);
      urlInstance.searchParams.set("loginRequired", "true");
      urlInstance.searchParams.set("moveDirect", "true");
      urlInstance.searchParams.set("ts", timestamp);
      urlInstance.searchParams.set("returnTo", encodeURIComponent(targetRxAdminRoute));
      Object.entries(additionalParams).forEach(([key, value]) => {
        const encodedValue = encodeTokenForUrl(value);
        if (encodedValue) {
          urlInstance.searchParams.set(key, encodedValue);
        }
      });
      window.location.href = urlInstance.toString();
      return;
    }

    if (accessToken) {
      // Create tokenized URL for seamless switching
      const adminUrl = createTokenizedUrl(
        `${import.meta.env.VITE_ADMIN_APP_DOMAIN}/administration/pharmacy-stores`,
        accessToken,
        'clinical',
        userInfo,
        { ...additionalParams, returnTo: targetRxAdminRoute }
      );

      // Log the token transfer for debugging
      logTokenTransfer('Header - Switch to Admin', 'clinical', true);
      // Redirect to admin with token
      window.location.href = adminUrl;
    } else {
      // Fallback: redirect without token (user will need to login)
      console.warn('No access token found, redirecting to admin login');
      const fallbackUrl = withReturnMetadata(import.meta.env.VITE_ADMIN_APP_DOMAIN + `${targetRxAdminRoute}?isUserLoggedInToClinical=true`);
      window.location.href = fallbackUrl;
    }
  };

  const handleProfileNavigation = (selected) => {
    // Store current location so we can return to it when exiting My Profile
    const currentPath = location.pathname + location.search;
    navigate(`/my-profile?selected=${selected}`, { 
      state: { from: currentPath } 
    });
  };

  /**
   * Handles user logout by dispatching logout action and navigating to login page
   * Ensures logout action completes before navigation and state cleanup
   */
  const handleLogout = async () => {
    try {
      // Wait for logout action to complete before cleanup and navigation
      await dispatch(logout());
      // Clear local state after successful logout
      setProfileAvatar(null);
      setUserName(null);
      // Navigate to login page after logout is successful
      navigate('/login');
    } catch (error) {
      console.error('Error during logout:', error);
      // Fallback cleanup and navigation even if logout fails
      setProfileAvatar(null);
      setUserName(null);
      navigate('/login');
    }
  };


  // Helper to check if profile section is active
  const isProfileSectionActive = () => {
    if (pathname.includes("/my-profile")) {
      return true;
    }
    return false;
  };

  const tooltip = (tooltipId) => {
    return (
      <NewToolTip
        id={tooltipId}
        render={() => (
          <div className="flex flex-col max-w-md p-2 bg-gray-20">
            <p className="font-normal text-left text-teal-300 text-body-sm">
              Coming Soon
            </p>
          </div>
        )}
      />
    );
  };

  return (
    <div className="relative z-50 h-[72px] flex items-center justify-between px-5 !pb-0 bg-white border-b border-b-gray-50">
      <div className="flex items-center">
        <img
          src="/logo/header/healthcompassRx.png"
          alt="healthcompassRx"
          title="healthcompassRx - Clinicals"
          className="min-w-[260px] h-[54px]"
        />
        <nav className="space-x-[12px] ml-[80px]">
          {legalNotAccepted ? (
            <>
              <span className="py-[20px] px-[12px] text-[16px] font-medium leading-[24px] text-gray-400 cursor-not-allowed opacity-50">
                Dashboard
              </span>
              <span className="py-[20px] px-[12px] text-[16px] font-medium leading-[24px] text-gray-400 cursor-not-allowed opacity-50">
                Patients
              </span>
              <span className="py-[20px] px-[12px] text-[16px] font-medium leading-[24px] text-gray-400 cursor-not-allowed opacity-50">
                Clinicals
              </span>
              <span className="py-[20px] px-[12px] text-[16px] font-medium leading-[24px] text-gray-400 cursor-not-allowed opacity-50">
                Scheduler
              </span>
              <span className="py-[20px] px-[12px] text-[16px] font-medium leading-[24px] text-gray-400 cursor-not-allowed opacity-50">
                Team
              </span>
              <span className="py-[20px] px-[12px] text-[16px] font-medium leading-[24px] text-gray-400 cursor-not-allowed opacity-50">
                Support
              </span>
            </>
          ) : (
            <>
              <Link
                // to="/dashboard"
                data-tooltip-id="dashboard-note"
                className={`${pathname === "/dashboard"
                  ? activeClass
                  : "text-gray-700 hover:border-b-[4px] hover:border-gray-20"
                  } py-[20px] px-[12px] text-[16px] font-medium leading-[24px] hover:bg-gray-20`}
              >
                Dashboard
              </Link>
              {tooltip("dashboard-note")}
              <Link
                to="/patients"
                onClick={(e) => {
                  if (checkAnyRunningService()) {
                    e.preventDefault();
                  }
                }}
                data-tooltip-id="service-alert"
                className={`${pathname.includes("/patient")
                  ? activeClass
                  : "text-gray-700 hover:border-b-[4px] hover:border-gray-20"
                  } py-[20px] px-[12px] text-[16px] font-medium leading-[24px] hover:bg-gray-20`}
              >
                Patients
              </Link>
              {checkAnyRunningService() && (
                <NewToolTip
                  id="service-alert"
                  render={() => (
                    <div className="flex flex-col max-w-md p-2 bg-gray-20">
                      <p className="font-normal text-left text-teal-300 text-body-sm">
                        Please complete or cancel the existing service,
                        <br /> in order to exit the patient.
                      </p>
                    </div>
                  )}
                />
              )}
              <Link
                // to="/clinicals"
                data-tooltip-id="clinicals-note"
                className={`${pathname === "/clinicals"
                  ? activeClass
                  : "text-gray-700 hover:border-b-[4px] hover:border-gray-20"
                  } py-[20px] px-[12px] text-[16px] font-medium leading-[24px] hover:bg-gray-20`}
              >
                Clinicals
              </Link>
              {tooltip("clinicals-note")}
              <Link
                // to="/scheduler"
                data-tooltip-id="scheduler-note"
                className={`${pathname === "/scheduler"
                  ? activeClass
                  : "text-gray-700 hover:border-b-[4px] hover:border-gray-20"
                  } py-2 px-[12px] text-[16px] font-medium leading-[24px] hover:bg-gray-20`}
              >
                Scheduler
              </Link>
              {tooltip("scheduler-note")}
              <Link
                // to="/team"
                data-tooltip-id="team-note"
                className={`${pathname === "/team"
                  ? activeClass
                  : "text-gray-700 hover:border-b-[4px] hover:border-gray-20"
                  } py-[20px] px-[12px] text-[16px] font-medium leading-[24px] hover:bg-gray-20`}
              >
                Team
              </Link>
              {tooltip("team-note")}
              <Link
                // to="/support"
                data-tooltip-id="support-note"
                className={`${pathname === "/support"
                  ? activeClass
                  : "text-gray-700 hover:border-b-[4px] hover:border-gray-20"
                  } py-[20px] px-[12px] text-[16px] font-medium leading-[24px] hover:bg-gray-20`}
              >
                Support
              </Link>
              {tooltip("support-note")}
            </>
          )}
        </nav>
      </div>

      <Menu as="div" className="relative inline-block text-left">
        {({ open }) => (
          <>
            <div>
              <Menu.Button className="flex justify-end py-2">
                <div className="flex items-center space-x-3">
                  <div className="flex items-center space-x-3">
                    <div className="flex flex-col items-end ">
                      <span className="font-semibold text-gray-900 text-body-sm">
                        {userName}
                      </span>
                      <span className="text-gray-600 text-body-sm">
                        {pharmacyName && storeNumber
                          ? `${pharmacyName} | Store ${storeNumber}`
                          : pharmacyName || ""}
                      </span>
                    </div>
                  </div>
                  <div
                    className={`${isProfileSectionActive()
                      ? "border-teal-600"
                      : "border-transparent"
                      } ${(open ? 'border-b-0' : 'border-b-4')}
                    flex items-center space-x-2 py-2 pr-2.5 hover:bg-gray-20`}
                  >
                    <img
                      src={
                        profileAvatar === null
                          ? "/icons/toDelete/Avatar.svg"
                          : profileAvatar
                      }
                      alt="Profile Avatar"
                      onClick={() => setShowUserOptions(true)}
                      className="px-2 border-r-2"
                    />
                    <img
                      src={"/icons/dark-pill.svg"}
                      alt="Admin Icon"
                      className="w-10 h-10"
                    />
                  </div>
                </div>
              </Menu.Button>

            </div>

            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <Menu.Items className="absolute right-[-4px] mt-2 origin-top-right bg-white rounded-md shadow-lg w-[320px] ring-1 ring-black ring-opacity-5">

                {/* Clinicals Dropdown (Current App) - only show dropdown if multiple environments */}
                {hasAnyClinicalAccess && hasSingleClinicalAccess ? (
                  <Menu.Item as="div">
                    {({ active }) => (
                      <div className="h-[55px] flex items-center justify-between w-full px-4 p-2 bg-teal-150 mb-2">
                        <div className="flex items-center">
                          <img
                            src={"/icons/clinicals-new.svg"}
                            alt="Clinical Icon"
                            className="pr-2"
                          />
                          <p className="text-white text-[18px]">
                            {`Clinicals | ${currentEnvironment}`}
                          </p>
                        </div>
                      </div>
                    )}
                  </Menu.Item>
                ) : hasAnyClinicalAccess && hasMultipleClinicalAccess ? (
                  <>
                    <Menu.Item as="div">
                      {({ active }) => (
                        <button
                          type="button"
                          className="h-[55px] flex items-center justify-between w-full px-4 p-2 bg-teal-150 hover:bg-teal-200 hover:text-white group mb-2"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setIsClinicalsSubmenuOpen((prev) => {
                              const next = !prev;
                              if (next) {
                                setIsAdministrationSubmenuOpen(false);
                                setIsReelAdministrationSubmenuOpen(false);
                              }
                              return next;
                            });
                          }}
                        >
                          <div className="flex items-center">
                            <img
                              src={"/icons/clinicals-new.svg"}
                              alt="Clinical Icon"
                              className="pr-2"
                            />
                            <p className="text-white text-[18px]">
                              {`Clinicals | ${currentEnvironment}`}
                            </p>
                          </div>
                          <svg
                            className={`pointer-events-none text-white transition-transform ${isClinicalsSubmenuOpen ? '-rotate-90' : ''}`}
                            width="20"
                            height="20"
                            viewBox="0 0 10 6"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                          >
                            <path
                              d="M1 1L5 5L9 1"
                              stroke="currentColor"
                              strokeWidth="0.8"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        </button>
                      )}
                    </Menu.Item>
                    {isClinicalsSubmenuOpen && (
                      <>
                        {hasAccessToDevClinical && (
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                type="button"
                                onClick={() => handleClinicalNavigation('dev')}
                                className={`${active ? 'bg-gray-20 text-teal-300' : 'text-teal-300'} flex items-center w-full text-left px-4 h-[44px] text-[16px] whitespace-nowrap border-b-[1px] border-gray-200 pl-14`}
                              >
                                Development
                              </button>
                            )}
                          </Menu.Item>
                        )}
                        {hasAccessToStagingClinical && (
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                type="button"
                                onClick={() => handleClinicalNavigation('staging')}
                                className={`${active ? 'bg-gray-20 text-teal-300' : 'text-teal-300'} flex items-center w-full text-left px-4 h-[44px] text-[16px] whitespace-nowrap border-b-[1px] border-gray-200 pl-14`}
                              >
                                Staging
                              </button>
                            )}
                          </Menu.Item>
                        )}
                        {hasAccessToProdClinical && (
                          <Menu.Item>
                            {({ active }) => (
                              <button
                                type="button"
                                onClick={() => handleClinicalNavigation('production')}
                                className={`${active ? 'bg-gray-20 text-teal-300' : 'text-teal-300'} flex items-center w-full text-left px-4 h-[44px] text-[16px] whitespace-nowrap border-b-[1px] border-gray-200 pl-14`}
                              >
                                Production
                              </button>
                            )}
                          </Menu.Item>
                        )}
                      </>
                    )}
                  </>
                ) : null}
                {/* Rx Administration Dropdown (External App) - only show for non-REEL users */}
                {canAccessRxAdministration && (
                  <>
                    {hasSingleAdminAccess ? (
                      <Menu.Item as="div">
                        {({ active }) => (
                          <button
                            type="button"
                            onClick={handleAdminSingleAccessClick}
                            className="h-[55px] flex items-center justify-between w-full px-4 p-2 bg-teal-25 hover:bg-teal-75 hover:text-teal-300 group mb-2"
                          >
                            <div className="flex items-center">
                              <img
                                src={"/icons/Rx-administration-new.svg"}
                                alt="Admin Icon"
                                className="pr-2"
                              />
                              <p className="text-teal-300 group-hover:text-teal-300 font-inter text-[18px]">
                                {adminPortalLabel}
                              </p>
                            </div>
                          </button>
                        )}
                      </Menu.Item>
                    ) : hasMultipleAdminAccess ? (
                      <>
                        <Menu.Item as="div">
                          {({ active }) => (
                            <button
                              type="button"
                              className="h-[55px] flex items-center justify-between w-full px-4 p-2 bg-teal-25 hover:bg-teal-75 hover:text-teal-300 group mb-2"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsAdministrationSubmenuOpen((prev) => {
                                  const next = !prev;
                                  if (next) {
                                    setIsClinicalsSubmenuOpen(false);
                                    setIsReelAdministrationSubmenuOpen(false);
                                  }
                                  return next;
                                });
                              }}
                            >
                              <div className="flex items-center">
                                <img
                                  src={"/icons/Rx-administration-new.svg"}
                                  alt="Admin Icon"
                                  className="pr-2"
                                />
                                <p className="text-teal-300 group-hover:text-teal-300 font-inter text-[18px]">
                                  {adminPortalLabel}
                                </p>
                              </div>
                              <svg
                                className={`pointer-events-none text-teal-300 group-hover:text-teal-300 transition-transform ${isAdministrationSubmenuOpen ? '-rotate-90' : ''}`}
                                width="20"
                                height="20"
                                viewBox="0 0 10 6"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M1 1L5 5L9 1"
                                  stroke="currentColor"
                                  strokeWidth="0.8"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                          )}
                        </Menu.Item>
                        {isAdministrationSubmenuOpen && (
                          <>
                            {hasAccessToDevAdmin && (
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    type="button"
                                    onClick={() => handleAdministrationNavigation('dev')}
                                    className={`${active ? 'bg-gray-20 text-teal-300' : 'text-teal-300'} flex items-center w-full text-left px-4 h-[44px] text-[16px] whitespace-nowrap border-b-[1px] border-gray-200 pl-14`}
                                  >
                                    Development
                                  </button>
                                )}
                              </Menu.Item>
                            )}
                            {hasAccessToStagingAdmin && (
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    type="button"
                                    onClick={() => handleAdministrationNavigation('staging')}
                                    className={`${active ? 'bg-gray-20 text-teal-300' : 'text-teal-300'} flex items-center w-full text-left px-4 h-[44px] text-[16px] whitespace-nowrap border-b-[1px] border-gray-200 pl-14`}
                                  >
                                    Staging
                                  </button>
                                )}
                              </Menu.Item>
                            )}
                            {hasAccessToProdAdmin && (
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    type="button"
                                    onClick={() => handleAdministrationNavigation('production')}
                                    className={`${active ? 'bg-gray-20 text-teal-300' : 'text-teal-300'} flex items-center w-full text-left px-4 h-[44px] text-[16px] whitespace-nowrap border-b-[1px] border-gray-200 pl-14`}
                                  >
                                    Production
                                  </button>
                                )}
                              </Menu.Item>
                            )}
                          </>
                        )}
                      </>
                    ) : (
                      null
                    )}
                  </>
                )}

                {/* REEL Administration Dropdown - only show if user is REEL member */}
                {canAccessReelAdministration && (
                  <>
                    {hasSingleAdminAccess ? (
                      <Menu.Item as="div">
                        {({ active }) => (
                          <button
                            type="button"
                            onClick={handleReelAdminSingleAccessClick}
                            className="h-[55px] flex items-center justify-between w-full px-4 p-2 bg-teal-25 hover:bg-teal-75 hover:text-teal-300 group mb-2"
                          >
                            <div className="flex items-center">
                              <img
                                src={"/icons/logos/reel-administration-new.svg"}
                                alt="REEL Administration Icon"
                                className="pr-2"
                              />
                              <p className="text-teal-300 group-hover:text-teal-300 font-inter text-[18px]">
                                REEL Administration
                              </p>
                            </div>
                          </button>
                        )}
                      </Menu.Item>
                    ) : hasMultipleAdminAccess ? (
                      <>
                        <Menu.Item as="div">
                          {({ active }) => (
                            <button
                              type="button"
                              className="h-[55px] flex items-center justify-between w-full px-4 p-2 bg-teal-25 hover:bg-teal-75 hover:text-teal-300 group mb-2"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                setIsReelAdministrationSubmenuOpen((prev) => {
                                  const next = !prev;
                                  if (next) {
                                    setIsClinicalsSubmenuOpen(false);
                                    setIsAdministrationSubmenuOpen(false);
                                  }
                                  return next;
                                });
                              }}
                            >
                              <div className="flex items-center">
                                <img
                                  src={"/icons/logos/reel-administration-new.svg"}
                                  alt="REEL Administration Icon"
                                  className="pr-2"
                                />
                                <p className="text-teal-300 group-hover:text-teal-300 font-inter text-[18px]">
                                  REEL Administration
                                </p>
                              </div>
                              <svg
                                className={`pointer-events-none text-teal-300 group-hover:text-teal-300 transition-transform ${isReelAdministrationSubmenuOpen ? '-rotate-90' : ''}`}
                                width="20"
                                height="20"
                                viewBox="0 0 10 6"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M1 1L5 5L9 1"
                                  stroke="currentColor"
                                  strokeWidth="0.8"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </svg>
                            </button>
                          )}
                        </Menu.Item>
                        {isReelAdministrationSubmenuOpen && (
                          <>
                            {hasAccessToDevAdmin && (
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    type="button"
                                    onClick={() => handleReelAdministrationClick('dev')}
                                    className={`${active ? 'bg-gray-20 text-teal-300' : 'text-teal-300'} flex items-center w-full text-left px-4 h-[44px] text-[16px] whitespace-nowrap border-b-[1px] border-gray-200 pl-14`}
                                  >
                                    Development
                                  </button>
                                )}
                              </Menu.Item>
                            )}
                            {hasAccessToStagingAdmin && (
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    type="button"
                                    onClick={() => handleReelAdministrationClick('staging')}
                                    className={`${active ? 'bg-gray-20 text-teal-300' : 'text-teal-300'} flex items-center w-full text-left px-4 h-[44px] text-[16px] whitespace-nowrap border-b-[1px] border-gray-200 pl-14`}
                                  >
                                    Staging
                                  </button>
                                )}
                              </Menu.Item>
                            )}
                            {hasAccessToProdAdmin && (
                              <Menu.Item>
                                {({ active }) => (
                                  <button
                                    type="button"
                                    onClick={() => handleReelAdministrationClick('production')}
                                    className={`${active ? 'bg-gray-20 text-teal-300' : 'text-teal-300'} flex items-center w-full text-left px-4 h-[44px] text-[16px] whitespace-nowrap border-b-[1px] border-gray-200 pl-14`}
                                  >
                                    Production
                                  </button>
                                )}
                              </Menu.Item>
                            )}
                          </>
                        )}
                      </>
                    ) : (
                      <Menu.Item as="div">
                        {({ active }) => (
                          <button
                            type="button"
                            onClick={() => handleReelAdministrationClick('dev')}
                            className="h-[55px] flex items-center justify-between w-full px-4 p-2 bg-teal-25 hover:bg-teal-75 hover:text-teal-300 group mb-2"
                          >
                            <div className="flex items-center">
                              <img
                                src={"/icons/logos/reel-administration-new.svg"}
                                alt="REEL Administration Icon"
                                className="pr-2"
                              />
                              <p className="text-teal-300 group-hover:text-teal-300 font-inter text-[18px]">
                                REEL Administration
                              </p>
                            </div>
                          </button>
                        )}
                      </Menu.Item>
                    )}
                  </>
                )}
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={() => handleProfileNavigation("profile")}
                      className={classNames(
                        "block w-full text-left px-4 h-[44px] text-[16px] flex items-center text-teal-300 mt-2",
                        active ? "bg-gray-20" : ""
                      )}
                    >
                      <img
                        src="/icons/profile-new.svg"
                        alt="user icon"
                        className="pr-2"
                      />
                      Profile
                    </button>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={() => handleProfileNavigation("settings")}
                      className={classNames(
                        "block w-full text-left px-4 h-[44px] text-[16px] flex items-center text-teal-300 mt-2",
                        active ? "bg-gray-20" : ""
                      )}
                    >
                      <img
                        src="/icons/settings-new.svg"
                        alt="settings icon"
                        className="pr-2"
                      />
                      Settings
                    </button>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={() => handleProfileNavigation("clinical-schedule")}
                      className={classNames(
                        "block w-full text-left px-4 h-[44px] text-[16px] flex items-center text-teal-300 mt-2",
                        active ? "bg-gray-20" : ""
                      )}
                    >
                      <img
                        src="/icons/clinical-schedule-new.svg"
                        alt="Clinical schedule icon"
                        className="pr-2"
                      />
                      Clinical Schedule
                    </button>
                  )}
                </Menu.Item>
                {/* Switch Tenant menu item - only visible for SuperUser role */}
                {hasUserRole(userRoles, UserRole.SuperUser) && (
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={() => navigate('/store-selection')}
                        className={classNames(
                          "block w-full text-left px-4 h-[44px] text-[16px] flex items-center text-teal-300",
                          active ? "bg-gray-20" : ""
                        )}
                      >
                        <svg
                          className="w-8 h-8 pr-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9v-9m0-9v9m0 9c-5 0-9-4-9-9s4-9 9-9m0 9c5 0 9-4 9-9s-4-9-9-9"
                          />
                        </svg>
                        Switch Tenant
                      </button>
                    )}
                  </Menu.Item>
                )}
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={() => handleProfileNavigation("about")}
                      className={classNames(
                        "block w-full text-left px-4 h-[44px] text-[16px] flex items-center text-teal-300 mt-2",
                        active ? "bg-gray-20" : ""
                      )}
                    >
                      <img
                        src="/icons/about-reel-new.svg"
                        alt="Page with question mark icon"
                        className="pr-2"
                      />
                      About REEL
                    </button>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      onClick={() => handleProfileNavigation("legal")}
                      className={classNames(
                        "block w-full text-left px-4 h-[44px] text-[16px] flex items-center text-teal-300 mt-2",
                        active ? "bg-gray-20" : ""
                      )}
                    >
                      <img
                        src="/icons/legal-new.svg"
                        alt="Page with check mark icon"
                        className="pr-2"
                      />
                      Legal
                    </button>
                  )}
                </Menu.Item>
                <Menu.Item>
                  {({ active }) => (
                    <button
                      className={`flex items-center w-full text-left px-4 py-2.5 text-[16px] text-red-200 cursor-pointer
                       h-[44px] mt-2 ${active ? "bg-red-50" : ""}`}
                      onClick={handleLogout}
                    >
                      <img
                        src="/icons/sign-out-new.svg"
                        alt="Sign out icon"
                        className="pr-2"
                      />
                      Sign out
                    </button>
                  )}
                </Menu.Item>
              </Menu.Items>
            </Transition>
          </>
        )}
      </Menu>
      <ConfirmationModalNew
        open={isSwitchModalOpen}
        title={"Switching Applications"}
        type="primary"
        cancelText="Stay in Clinicals"
        confirmText={
          switchTarget === 'reelAdmin'
            ? 'Proceed to REEL Administration'
            : 'Proceed to Rx Administration'
        }
        cancelBtnType="primary-outline"
        className={"!w-[630px] !max-w-[630px]"}
        content={() => (
          <div className="text-left">
            <p className="text-gray-800 font-normal">
              Switching applications requires a separate log-in. Your current session will be saved at this point for 15 minutes; after that it will be an automatic log out due to inactivity.
            </p>
            <p className="mt-4 text-gray-500 font-normal">
              To return, simply log back into the Clinical Application within that time.
            </p>
          </div>
        )}
        handleClose={handleModalClose}
        onCross={handleModalClose}
        confirmAction={switchTarget === 'reelAdmin' ? handleConfirmReelAdminSwitch : handleConfirmAdminSwitch}
        cancelAction={handleStayInClinical}
      />

    </div>
  );
};

export default Header;
